import { MessageResponseSchema, MIN_PASSWORD_LENGTH } from '@sbc/domain-models/admin';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { ApiError, apiSend } from '@/lib/api/client';

// Serves both the welcome (set-initial-password) and forgot-password links — the
// token in the URL is what distinguishes them, the form is identical.
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiSend(
        'POST',
        '/api/auth/reset',
        { token, newPassword: password },
        MessageResponseSchema,
      );
      void navigate({ to: '/login' });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not set your password. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center p-8">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Set your password</CardTitle>
          <CardDescription>Choose a password to finish signing in.</CardDescription>
        </CardHeader>
        <CardContent>
          {token ? (
            <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Saving…' : 'Set password'}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-[var(--color-destructive)]">
              This link is missing its token. Request a new one from{' '}
              <Link to="/forgot-password" className="underline">
                forgot password
              </Link>
              .
            </p>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
